import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Seo } from '../components/Seo';
import { PageHero } from '../components/PageHero';
import { CtaBand } from '../components/CtaBand';
import { images, site } from '../data/site';
export function About() {
  return <>
      <Seo title="À propos de Goelst France — rails à rideaux depuis 1978" description="Goelst France S.A.R.L : une culture de la précision appliquée aux rails à rideaux depuis 1978. Découvrez notre approche du projet, nos exigences de qualité et notre manière de travailler avec les architectes et les exploitants." path="/a-propos" image={images.warmLiving} data-id="element-115" data-mp="2ejrp55islz3s" />

      <PageHero breadcrumb="À propos" title={`Une culture de la précision appliquée aux rails à rideaux depuis ${site.since}`} lead="Goelst conçoit des systèmes de rideaux dont la valeur se mesure sur la durée : un geste fluide, une course silencieuse, un mécanisme qui se fait oublier. Goelst France S.A.R.L porte cette exigence sur les projets français, depuis Bignoles, en Provence-Alpes-Côte d’Azur." image={images.warmLiving} imageAlt="Intérieur chaleureux avec rideaux et voilages suspendus à un rail intégré au plafond" data-id="element-116" data-mp="2x86iw0khvr8a" />

      <section className="bg-white" aria-labelledby="histoire-titre" data-id="element-117" data-mp="1yivivk7gifag">
        <div className="mx-auto grid max-w-content gap-12 px-6 py-16 md:py-20 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-118" data-mp="1ylq43dyao44x">
          <div data-id="element-119" data-mp="28hkremdgqne1">
            <h2 id="histoire-titre" className="text-3xl font-semibold leading-tight tracking-tight text-ink md:text-4xl" data-id="element-120" data-mp="28i2yyf5kso19">
              Une expérience construite depuis {site.since}
            </h2>
          </div>
          <div className="space-y-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-121" data-mp="2i8fetu2g3t12">
            <p data-id="element-122" data-mp="2w6m4imx8qmpn">
              Goelst travaille les systèmes de rails à rideaux depuis {site.since}. Une durée de cette nature ne se
              résume pas à une date sur une page : elle correspond à des décennies d’ajustements sur des points que
              personne ne remarque quand ils sont bien traités — la forme intérieure d’un profil, la géométrie d’un
              chariot, la tenue d’une fixation, la façon dont deux longueurs de rail se rejoignent.
            </p>
            <p data-id="element-123" data-mp="22m19i3s7lru8">
              Cette accumulation d’expérience explique la signature de la marque, « European Quality &amp; Design » :
              une approche où la mécanique et l’esthétique sont traitées ensemble, et non l’une après l’autre. Le rail
              doit fonctionner impeccablement et, dans le même temps, savoir disparaître dans l’architecture.
            </p>
            <p data-id="element-124" data-mp="2gjpvjey0j50v">
              Goelst France S.A.R.L est l’entité qui porte cette gamme sur le marché français. Notre rôle n’est pas
              seulement de fournir un produit : c’est d’aider les prescripteurs et les exploitants à choisir la bonne
              configuration, au bon moment du projet, et à éviter les compromis coûteux en fin de chantier.
            </p>
            <p data-id="element-125" data-mp="jo4yqhl3cz0c">
              Pour l’ensemble de la gamme, les gabarits et les visuels de produits, le site international du groupe est
              disponible à l’adresse{' '}
              <a href={site.website} target="_blank" rel="noopener noreferrer" className="font-medium text-signal underline decoration-signal/40 underline-offset-4 transition-colors duration-150 ease-smooth hover:decoration-signal" data-id="element-126" data-mp="9ws6hovds6ts">
                goelst-international.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-bone" aria-labelledby="valeurs-titre" data-id="element-127" data-mp="sq4i3gjkwf6g">
        <div className="mx-auto max-w-content px-6 py-16 md:py-20" data-id="element-128" data-mp="om6mhftgpmi5">
          <div className="max-w-3xl" data-id="element-129" data-mp="3dfbq62zzu4cz">
            <h2 id="valeurs-titre" className="text-3xl font-semibold leading-tight tracking-tight text-ink md:text-4xl" data-id="element-130" data-mp="3dc63eueu7ekf">
              Ce sur quoi nous ne transigeons pas
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-131" data-mp="112o5jdcc70qi">
              Trois principes structurent notre manière de traiter un projet, du premier plan reçu jusqu’à la mise en
              service des rideaux.
            </p>
          </div>
          <div className="mt-12 grid gap-px bg-line md:grid-cols-3" data-id="element-132" data-mp="w44itapx5kx3">
            {[{
            t: 'La précision avant tout',
            d: 'Un rail se juge au millimètre. Les longueurs, les rayons de cintrage, les entraxes de fixation et les jonctions sont définis avant la fabrication, à partir des plans ou d’un relevé sur site. C’est cette rigueur en amont qui rend la pose simple.'
          }, {
            t: 'La durée d’usage',
            d: 'Un système de rideaux est manipulé des milliers de fois. Nous privilégions donc des composants remplaçables, une maintenance compréhensible et des configurations qui permettent de déposer le textile sans démonter l’installation.'
          }, {
            t: 'La discrétion',
            d: 'Le rail ne doit pas concurrencer l’architecture. Selon le projet, il s’intègre en gorge, en retombée de plafond ou en coffre menuisé, jusqu’à devenir invisible — le rideau semble alors partir directement du plafond.'
          }].map(v => <article key={v.t} className="flex h-full flex-col bg-white p-8 md:p-10" data-id="element-133" data-mp="39b2qr8vdgih3">
                <h3 className="text-xl font-semibold tracking-tight text-ink" data-id="element-134" data-mp="34crgwy52u7yv">{v.t}</h3>
                <p className="mt-4 text-[16px] leading-relaxed text-ink-soft" data-id="element-135" data-mp="3j1f8yufprzk2">{v.d}</p>
              </article>)}
          </div>
        </div>
      </section>

      <section className="bg-white" aria-labelledby="methode-titre" data-id="element-136" data-mp="3e9e1dlthqx0d">
        <div className="mx-auto grid max-w-content gap-12 px-6 py-16 md:py-20 lg:grid-cols-[1fr_0.9fr] lg:items-center" data-id="element-137" data-mp="2qk2vxr174efw">
          <div data-id="element-138" data-mp="2lli2qbkd2v0y">
            <h2 id="methode-titre" className="text-3xl font-semibold leading-tight tracking-tight text-ink md:text-4xl" data-id="element-139" data-mp="2ljxa4rhlxm5q">
              Notre manière de travailler avec les prescripteurs
            </h2>
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-140" data-mp="3f9e7378guhgm">
              <p data-id="element-141" data-mp="3k38wmrar5qnm">
                Un projet de rideaux se joue à deux moments : au dessin, quand on décide s’il y aura une réservation en
                plafond et une alimentation électrique, puis à la pose, quand ces décisions deviennent irréversibles.
                Entre les deux, il y a rarement beaucoup de marge de manœuvre.
              </p>
              <p data-id="element-142" data-mp="31bpx4ufxsuqc">
                Nous intervenons donc volontiers au stade des plans d’exécution. À ce moment, quelques échanges
                suffisent à valider la faisabilité d’un rail intégré, à réserver la bonne hauteur de retombée, à choisir
                le côté d’arrivée du moteur, ou à décider qu’un double rang est nécessaire pour superposer voilage et
                occultant.
              </p>
              <p data-id="element-143" data-mp="261uj7jwmxtw1">
                Sur les projets importants, nous procédons souvent par pièce témoin : une chambre, un bureau ou une
                suite est équipée en premier, validée avec la maîtrise d’ouvrage, puis dupliquée à l’identique. Cette
                méthode réduit les incertitudes et rend le déploiement beaucoup plus rapide.
              </p>
              <p data-id="element-144" data-mp="1nc4nmul1t432">
                Enfin, nous restons joignables pendant le chantier. Un détail de fixation, une jonction imprévue, une
                largeur modifiée : mieux vaut une question posée au bon moment qu’une adaptation improvisée sur place.
              </p>
            </div>
            <Link to="/solutions" className="mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-ink underline decoration-line underline-offset-4 transition-colors duration-150 ease-smooth hover:text-signal" data-id="element-145" data-mp="18f588kjyg6y1">
              Découvrir nos solutions de rails à rideaux
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-146" data-mp="2bnallro6req7" />
            </Link>
          </div>
          <figure className="overflow-hidden bg-bone" data-id="element-147" data-mp="2gn5ajpoehu8m">
            <img src={images.sheerWhite} alt="Voilages blancs toute hauteur devant une grande baie, portés par un rail plafond continu" className="h-full w-full object-cover" loading="lazy" width={1000} height={1000} data-id="element-148" data-mp="2gozub0nxrgb4" />
          </figure>
        </div>
      </section>

      <section className="border-y border-line bg-ink text-white" aria-labelledby="implantation-titre" data-id="element-149" data-mp="1xx73ncwca76k">
        <div className="mx-auto grid max-w-content gap-12 px-6 py-16 md:py-20 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-150" data-mp="1jxxxl61uktgb">
          <div data-id="element-151" data-mp="1nyq802mgh4e6">
            <h2 id="implantation-titre" className="text-3xl font-semibold leading-tight tracking-tight md:text-4xl" data-id="element-152" data-mp="1o0ast0iyx8yu">
              Notre implantation en Provence
            </h2>
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-white/70" data-id="element-153" data-mp="3ibpjpknpteg6">
              <p data-id="element-154" data-mp="16pp239mai2i0">
                Goelst France S.A.R.L est installée au {site.address.street}, à {site.address.city} (
                {site.address.postalCode}), dans le Var. Cette position, entre l’arrière-pays varois et le littoral,
                correspond à une réalité de terrain : une grande partie des projets hôteliers, résidentiels haut de gamme
                et de santé de la région se situe à quelques dizaines de kilomètres.
              </p>
              <p data-id="element-155" data-mp="1le1zmb10ztem">
                Nous travaillons cependant sur l’ensemble du territoire français, en lien direct avec les architectes,
                les entreprises générales, les tapissiers et les installateurs. Les échanges techniques se font le plus
                souvent par téléphone et par courriel, sur la base de plans — un mode de fonctionnement efficace, y
                compris à distance.
              </p>
            </div>
          </div>
          <dl className="grid gap-6 self-start border-t border-white/20 pt-6 sm:grid-cols-2 lg:grid-cols-1" data-id="element-156" data-mp="29z7hngz9bk">
            <div data-id="element-157" data-mp="3cbwn4pcez2wf">
              <dt className="text-[13px] uppercase tracking-[0.16em] text-white/50" data-id="element-158" data-mp="37ewgodbnjy4s">Adresse</dt>
              <dd className="mt-2 text-[16px] leading-relaxed" data-id="element-159" data-mp="33db83465i60e">{site.addressLine}</dd>
            </div>
            <div data-id="element-160" data-mp="2ye83tlxzw3th">
              <dt className="text-[13px] uppercase tracking-[0.16em] text-white/50" data-id="element-161" data-mp="2yeri8sofsa2r">Activité</dt>
              <dd className="mt-2 text-[16px]" data-id="element-162" data-mp="3v50h6btjnhrk">{site.category}</dd>
            </div>
            <div data-id="element-163" data-mp="3r35jdhatnmij">
              <dt className="text-[13px] uppercase tracking-[0.16em] text-white/50" data-id="element-164" data-mp="3r2ne5v66lxyh">Horaires</dt>
              <dd className="mt-2 text-[16px] leading-relaxed" data-id="element-165" data-mp="3mbnl5106o019">
                Lundi – Vendredi : 09:00 – 18:00
                <br data-id="element-166" data-mp="3hc15ym0so9pt" />
                Samedi et dimanche : fermé
              </dd>
            </div>
            <div data-id="element-167" data-mp="io5bk8z0fam9">
              <dt className="text-[13px] uppercase tracking-[0.16em] text-white/50" data-id="element-168" data-mp="ion4avmgr6pf">Contact</dt>
              <dd className="mt-2 text-[16px] leading-relaxed" data-id="element-169" data-mp="duspo6lpthx7">
                <a className="underline underline-offset-4 hover:text-signal" href={site.phoneHref} data-id="element-170" data-mp="l81mslb72ftf">
                  {site.phoneDisplay}
                </a>
                <br data-id="element-171" data-mp="q8pfrcizh6qa" />
                <a className="underline underline-offset-4 hover:text-signal" href={site.emailHref} data-id="element-172" data-mp="v5xxxd44tarj">
                  {site.email}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <CtaBand title="Parlons de votre projet" text="Que vous soyez au stade de l’esquisse ou en phase de chantier, un échange court suffit souvent à lever les principales incertitudes sur le choix du rail et son intégration." data-id="element-173" data-mp="145cnmach5haz" />
    </>;
}